/**
 * Audio de los videos del inicio (Optioutlet).
 *
 * Dos bandas, dos tratos distintos, los dos pedidos por Brandon el 2026-10-14:
 *
 * - **La banda a sangre (`video_ambiente`) suena sola.** «Me gustaría que el
 *   default sea que se reproduzca el audio… porque no se escucha sino hasta
 *   que bajas al video y le das play.» Aquí abajo está explicado por qué eso
 *   no se puede dar de entrada y qué se hace en su lugar.
 * - **El spotlight (`spotlight_combo`) va mudo y sin control.** «Como es un
 *   stop motion queda mejor a manera de gif.» No lleva botón: no hay forma
 *   de encenderle el audio desde la página.
 *
 * ## Por qué el primero no puede nacer sonando
 *
 * Ningún navegador deja que un <video> empiece con sonido sin que el
 * visitante haya tocado la página antes; si lleva `autoplay`, tiene que
 * llevar `muted`. No es un ajuste del tema ni algo que se pueda desactivar.
 *
 * Lo más cerca que se llega, y lo que hace este guion:
 *
 *   1. En cuanto la banda entra en pantalla, se intenta quitar el mudo.
 *   2. Si el navegador lo permite --porque el visitante ya hizo clic en algo,
 *      o porque suele ver video en este sitio--, suena sin que nadie pida nada.
 *   3. Si no lo permite, el video se deja mudo para que la imagen no se
 *      detenga, y se espera al primer gesto del visitante en cualquier parte
 *      de la página. Con ese gesto se vuelve a intentar.
 *
 * **Si el visitante apaga el sonido con el botón, ya no se vuelve a encender
 * solo.** Insistir después de que alguien dijo que no es justo lo que estas
 * reglas del navegador existen para evitar.
 *
 * ## Dónde vive y por qué
 *
 * En assets/ y no en los snippets del tema: `snippets/video.liquid` y
 * `snippets/background-media.liquid` son de Horizon y los pisaría una
 * actualización. Ver [[horizon-files-modified]].
 *
 * El enganche va por sufijo de sección, igual que la hoja de estilos, porque
 * Shopify numera las secciones de una plantilla JSON y el número cambia
 * entre temas y publicaciones.
 */

/**
 * `ancla` dice de qué elemento cuelga el botón, y no es el mismo sitio en
 * las dos: en la banda a sangre el video es el fondo de la sección entera,
 * mientras que en el spotlight vive en una sola columna y el botón tiene que
 * quedarse encima de ESA columna --en móvil la columna de texto va debajo y
 * el botón acabaría sobre las letras--.
 */
const BANDAS = [
  { seccion: '[id$="__video_ambiente"]', ancla: (seccion) => seccion, conSonido: true },
  {
    seccion: '[id$="__spotlight_combo"]',
    ancla: (seccion) => seccion.querySelector('deferred-media') || seccion,
    conSonido: false,
  },
];

const BOCINA_ENCENDIDA = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M19 5a10 10 0 0 1 0 14"/></svg>`;
const BOCINA_APAGADA = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="m17 9 5 6"/><path d="m22 9-5 6"/></svg>`;

/** Los videos que SÍ pueden sonar, para poder apagarse entre ellos. */
const montados = [];
/** Los que están esperando un gesto del visitante para encenderse. */
const esperandoGesto = new Set();
let gestoArmado = false;

function silenciarLasDemas(actual) {
  for (const otra of montados) {
    // El editor de temas vuelve a pintar secciones enteras: las fichas de
    // un video que ya no está en la página se descartan al pasar.
    if (!otra.video.isConnected) continue;
    if (otra.video === actual || otra.video.muted) continue;
    otra.video.muted = true;
    otra.pintar();
  }
}

function armarGesto() {
  if (gestoArmado) return;
  gestoArmado = true;
  const alGesto = () => {
    gestoArmado = false;
    for (const ficha of [...esperandoGesto]) {
      esperandoGesto.delete(ficha);
      if (ficha.enPantalla) encender(ficha);
    }
  };
  for (const evento of ['pointerdown', 'keydown', 'touchstart']) {
    document.addEventListener(evento, alGesto, { once: true, passive: true });
  }
}

function encender(ficha) {
  const { video } = ficha;
  if (ficha.apagadoAProposito || !video.muted) return;

  silenciarLasDemas(video);
  video.muted = false;
  ficha.pintar();

  const promesa = video.play();
  if (promesa && typeof promesa.catch === 'function') {
    promesa.catch(() => {
      // El navegador todavía no lo permite. Se vuelve a dejar mudo --si no,
      // el video se queda congelado-- y se espera un gesto para reintentar.
      video.muted = true;
      video.play().catch(() => {});
      ficha.pintar();
      esperandoGesto.add(ficha);
      armarGesto();
    });
  }
}

function montar(seccion, ancla, conSonido) {
  const video = seccion.querySelector('video');
  if (!video) return;

  // La barra nativa sólo la pinta una de las dos bandas --`snippets/video.liquid`
  // le pasa `controls: true` cuando nadie pide lo contrario--. Se quita en las
  // dos: la de arriba lleva su propio botón y la de abajo no lleva ninguno.
  // Va por JavaScript y no por CSS porque ::-webkit-media-controls no existe
  // en Firefox; el atributo sí se va en todos.
  video.controls = false;
  video.removeAttribute('controls');

  if (!conSonido) {
    // Mudo y sin manera de dejar de serlo. La pista sigue dentro del archivo;
    // simplemente no hay con qué encenderla.
    video.muted = true;
    video.addEventListener('volumechange', () => {
      if (!video.muted) video.muted = true;
    });
    return;
  }

  if (ancla.querySelector(':scope > .ol-sonido')) return;

  const boton = document.createElement('button');
  boton.type = 'button';
  boton.className = 'ol-sonido';

  const ficha = { video, pintar: () => {}, apagadoAProposito: false, enPantalla: false };

  ficha.pintar = () => {
    const mudo = video.muted;
    boton.innerHTML = mudo ? BOCINA_APAGADA : BOCINA_ENCENDIDA;
    boton.setAttribute('aria-label', mudo ? 'Activar el sonido del video' : 'Silenciar el video');
    boton.setAttribute('aria-pressed', mudo ? 'false' : 'true');
  };

  boton.addEventListener('click', () => {
    if (video.muted) {
      ficha.apagadoAProposito = false;
      encender(ficha);
    } else {
      // Apagarlo a mano es una decisión del visitante y se respeta: de aquí
      // en adelante no se vuelve a encender solo.
      ficha.apagadoAProposito = true;
      esperandoGesto.delete(ficha);
      video.muted = true;
      ficha.pintar();
    }
  });

  // Red de seguridad: si algo enciende este audio sin pasar por el botón,
  // igual se apagan los demás y los botones se ponen al día.
  video.addEventListener('volumechange', () => {
    if (!video.muted) silenciarLasDemas(video);
    ficha.pintar();
  });

  ficha.pintar();
  ancla.appendChild(boton);
  montados.push(ficha);

  // El sonido se enciende cuando la banda está a la vista, no antes: arrancar
  // el audio de algo que todavía no se ve --la banda es la quinta sección--
  // se oye como un ruido sin origen.
  if ('IntersectionObserver' in window) {
    const vigia = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          ficha.enPantalla = entrada.isIntersecting;
          if (entrada.isIntersecting) encender(ficha);
        }
      },
      { threshold: 0.35 }
    );
    vigia.observe(seccion);
  } else {
    ficha.enPantalla = true;
    encender(ficha);
  }
}

function arrancar(raiz = document) {
  for (const { seccion: selector, ancla, conSonido } of BANDAS) {
    for (const seccion of raiz.querySelectorAll(selector)) {
      const destino = ancla(seccion);
      if (destino) montar(seccion, destino, conSonido);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => arrancar(), { once: true });
} else {
  arrancar();
}

// El editor de temas vuelve a pintar la sección al tocarle un ajuste.
document.addEventListener('shopify:section:load', (evento) => {
  const raiz = evento.target;
  if (raiz?.querySelectorAll) arrancar(raiz);
});
